import React, { FC, useMemo, useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "../../../assets/icons";

interface Props {
	isLoading?: boolean;
	placeholder?: string;
	debounceMilliseconds?: number;
	onChange: (value: string) => void;
}

export const SearchBar: FC<Props> = ({ isLoading, placeholder, debounceMilliseconds = 500, onChange }) => {
	const { t } = useTranslation("common");
	const [search, setSearch] = useState("");

	const debouncedOnChange = useMemo(
		() => debounce((value: string) => onChange?.(value), debounceMilliseconds),
		[debounceMilliseconds, onChange]
	);

	const handleChange = (value: string) => {
		setSearch(value);
		debouncedOnChange(value);
	};

	const handleClear = () => {
		setSearch("");
		debouncedOnChange.cancel();
		onChange?.("");
	};

	return (
		<TextField
			fullWidth
			value={search}
			slotProps={{
				input: {
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					),
					endAdornment: search.length > 0 && (
						<InputAdornment position="end">
							<IconButton size="small" onClick={handleClear}>
								<ClearIcon />
							</IconButton>
						</InputAdornment>
					),
				},
			}}
			disabled={isLoading}
			onChange={(e) => handleChange(e.target.value)}
			label={t("common:search")}
			placeholder={placeholder}
			variant="outlined"
			size="small"
			sx={{ marginY: 2 }}
		/>
	);
};
